import {} from 'react-redux';

import { Action } from '@reduxjs/toolkit';

import * as actionTypes from '../constants/smses';

import { updateObject } from '../../../../shared/utils/utility';

import { AppAction } from '../../../../store';

import {
  CheckPhoneNumberResult,
  GetUpdatesResult,
  InsuredPhoneNumbersCollection,
  InsuredPhonesCollection,
  MessageControlParameters,
  MessagesCollection,
  SMSe,
  SmsMigrationData,
  SmsChat,
} from '../../models';

export interface SMSesState {
  messages: MessagesCollection | null;
  messagesError: any | null;
  messagesLoading: boolean;

  insuredsPhones: InsuredPhonesCollection | null;
  insuredsPhonesError: any | null;
  insuredsPhonesLoading: boolean;

  sendMessageResult: SMSe | null;
  sendMessageResultError: any | null;
  sendMessageResultLoading: boolean;

  messageControlParameters: MessageControlParameters | null;
  messageControlParametersError: any | null;
  messageControlParametersLoading: boolean;

  allInsuredNumbers: InsuredPhoneNumbersCollection | null;
  allInsuredNumbersError: any | null;
  allInsuredNumbersLoading: boolean;

  hasUpdateResult: GetUpdatesResult | null;
  hasUpdateError: any | null;
  hasUpdateLoading: boolean;

  localMessages: MessagesCollection | null;

  sendMassMessagesResult: boolean;
  sendMassMessagesResultError: any | null;
  sendMassMessagesResultLoading: boolean;

  checkPhoneNumber: CheckPhoneNumberResult | null;
  checkPhoneNumberError: any | null;
  checkPhoneNumberLoading: boolean;

  startMigrationResult?: SmsMigrationData;
  startMigrationError: any | null;
  startMigrationLoading: boolean;

  saveMigrationResult: boolean;
  saveMigrationError: any | null;
  saveMigrationLoading: boolean;

  selectedChatItem: SmsChat | undefined;
}

const initialState: SMSesState = {
  messages: null,
  messagesError: null,
  messagesLoading: false,

  insuredsPhones: null,
  insuredsPhonesError: null,
  insuredsPhonesLoading: false,

  sendMessageResult: null,
  sendMessageResultError: null,
  sendMessageResultLoading: false,

  messageControlParameters: null,
  messageControlParametersError: null,
  messageControlParametersLoading: false,

  allInsuredNumbers: null,
  allInsuredNumbersError: null,
  allInsuredNumbersLoading: false,

  hasUpdateResult: null,
  hasUpdateError: null,
  hasUpdateLoading: false,

  localMessages: null,

  sendMassMessagesResult: false,
  sendMassMessagesResultError: null,
  sendMassMessagesResultLoading: false,

  checkPhoneNumber: null,
  checkPhoneNumberError: null,
  checkPhoneNumberLoading: false,

  startMigrationResult: undefined,
  startMigrationError: null,
  startMigrationLoading: false,

  saveMigrationResult: false,
  saveMigrationError: null,
  saveMigrationLoading: false,

  selectedChatItem: undefined,
};

const getMessagesStart = (state: SMSesState, action: AppAction<MessagesCollection>) => {
  return updateObject(state, {
    messagesError: null,
    messagesLoading: true,
  });
};

const getMessagesSuccess = (state: SMSesState, action: AppAction<MessagesCollection>) => {
  return updateObject(state, {
    messages: action.data,
    messagesError: null,
    messagesLoading: false,
  });
};

const getMessagesFail = (state: SMSesState, action: AppAction<MessagesCollection>) => {
  return updateObject(state, {
    messages: null,
    messagesError: action.error,
    messagesLoading: false,
  });
};

const setMessages = (state: SMSesState, action: AppAction<MessagesCollection>) => {
  return updateObject(state, {
    messages: action.data,
  });
};

const getInsuredsPhonesStart = (state: SMSesState, action: AppAction<InsuredPhonesCollection>) => {
  return updateObject(state, {
    insuredsPhonesError: null,
    insuredsPhonesLoading: true,
  });
};

const getInsuredsPhonesSuccess = (state: SMSesState, action: AppAction<InsuredPhonesCollection>) => {
  return updateObject(state, {
    insuredsPhones: action.data,
    insuredsPhonesError: null,
    insuredsPhonesLoading: false,
  });
};

const getInsuredsPhonesFail = (state: SMSesState, action: AppAction<InsuredPhonesCollection>) => {
  return updateObject(state, {
    insuredsPhones: null,
    insuredsPhonesError: action.error,
    insuredsPhonesLoading: false,
  });
};

const getSendMessageResultStart = (state: SMSesState, action: AppAction<SMSe>) => {
  return updateObject(state, {
    sendMessageResultError: null,
    sendMessageResultLoading: true,
  });
};

const getSendMessageResultSuccess = (state: SMSesState, action: AppAction<SMSe>) => {
  return updateObject(state, {
    sendMessageResult: action.data,
    sendMessageResultError: null,
    sendMessageResultLoading: false,
  });
};

const getSendMessageResultFail = (state: SMSesState, action: AppAction<SMSe>) => {
  return updateObject(state, {
    sendMessageResult: null,
    sendMessageResultError: action.error,
    sendMessageResultLoading: false,
  });
};

const getMessageControlParametersStart = (state: SMSesState, action: AppAction<MessageControlParameters>) => {
  return updateObject(state, {
    messageControlParametersError: null,
    messageControlParametersLoading: true,
  });
};

const getMessageControlParametersSuccess = (state: SMSesState, action: AppAction<MessageControlParameters>) => {
  return updateObject(state, {
    messageControlParameters: action.data,
    messageControlParametersError: null,
    messageControlParametersLoading: false,
  });
};

const getMessageControlParametersFail = (state: SMSesState, action: AppAction<MessageControlParameters>) => {
  return updateObject(state, {
    messageControlParameters: null,
    messageControlParametersError: action.error,
    messageControlParametersLoading: false,
  });
};

const getAllInsuredNumbersStart = (state: SMSesState, action: AppAction<InsuredPhoneNumbersCollection>) => {
  return updateObject(state, {
    allInsuredNumbersError: null,
    allInsuredNumbersLoading: true,
  });
};

const getAllInsuredNumbersSuccess = (state: SMSesState, action: AppAction<InsuredPhoneNumbersCollection>) => {
  return updateObject(state, {
    allInsuredNumbers: action.data,
    allInsuredNumbersError: null,
    allInsuredNumbersLoading: false,
  });
};

const getAllInsuredNumbersFail = (state: SMSesState, action: AppAction<InsuredPhoneNumbersCollection>) => {
  return updateObject(state, {
    allInsuredNumbers: null,
    allInsuredNumbersError: action.error,
    allInsuredNumbersLoading: false,
  });
};

const getUpdatesStart = (state: SMSesState, action: AppAction<GetUpdatesResult>) => {
  return updateObject(state, {
    hasUpdateResult: null,
    hasUpdateError: null,
    hasUpdateLoading: true,
  });
};

const getUpdatesSuccess = (state: SMSesState, action: AppAction<GetUpdatesResult>) => {
  return updateObject(state, {
    hasUpdateResult: action.data,
    hasUpdateError: null,
    hasUpdateLoading: false,
  });
};

const getUpdatesFail = (state: SMSesState, action: AppAction<GetUpdatesResult>) => {
  return updateObject(state, {
    hasUpdateResult: null,
    hasUpdateError: action.error,
    hasUpdateLoading: false,
  });
};

const setLocalMessages = (state: SMSesState, action: AppAction<MessagesCollection>) => {
  return updateObject(state, {
    localMessages: action.data,
  });
};

const sendMassMessagesStart = (state: SMSesState, action: AppAction<boolean>) => {
  return updateObject(state, {
    sendMassMessagesResultError: null,
    sendMassMessagesResultLoading: true,
  });
};

const sendMassMessagesSuccess = (state: SMSesState, action: AppAction<boolean>) => {
  return updateObject(state, {
    sendMassMessagesResult: action.data,
    sendMassMessagesResultError: null,
    sendMassMessagesResultLoading: false,
  });
};

const sendMassMessagesFail = (state: SMSesState, action: AppAction<boolean>) => {
  return updateObject(state, {
    sendMassMessagesResult: null,
    sendMassMessagesResultError: action.error,
    sendMassMessagesResultLoading: false,
  });
};

const setUpdates = (state: SMSesState, action: AppAction<GetUpdatesResult>) => {
  return updateObject(state, {
    hasUpdateResult: action.data,
    hasUpdateError: null,
    hasUpdateLoading: false,
  });
};

const checkPhoneNumberStart = (state: SMSesState, action: AppAction<CheckPhoneNumberResult>) => {
  return updateObject(state, {
    checkPhoneNumberError: null,
    checkPhoneNumberLoading: true,
  });
};

const checkPhoneNumberSuccess = (state: SMSesState, action: AppAction<CheckPhoneNumberResult>) => {
  return updateObject(state, {
    checkPhoneNumber: action.data,
    checkPhoneNumberError: null,
    checkPhoneNumberLoading: false,
  });
};

const checkPhoneNumberFail = (state: SMSesState, action: AppAction<CheckPhoneNumberResult>) => {
  return updateObject(state, {
    checkPhoneNumber: null,
    checkPhoneNumberError: action.error,
    checkPhoneNumberLoading: false,
  });
};

const setMessageControlParameters = (state: SMSesState, action: AppAction<MessageControlParameters>) => {
  return updateObject(state, {
    messageControlParameters: action.data,
    messageControlParametersError: null,
    messageControlParametersLoading: false,
  });
};

const startMigrationStart = (state: SMSesState, action: AppAction<SmsMigrationData>) => {
  return updateObject(state, {
    startMigrationResult: null,
    startMigrationError: null,
    startMigrationLoading: true,
  });
};

const startMigrationSuccess = (state: SMSesState, action: AppAction<SmsMigrationData>) => {
  return updateObject(state, {
    startMigrationResult: action.data,
    startMigrationError: null,
    startMigrationLoading: false,
  });
};

const startMigrationFail = (state: SMSesState, action: AppAction<SmsMigrationData>) => {
  return updateObject(state, {
    startMigrationResult: null,
    startMigrationError: action.error,
    startMigrationLoading: false,
  });
};

const saveMigrationStart = (state: SMSesState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveMigrationResult: null,
    saveMigrationError: null,
    saveMigrationLoading: true,
  });
};

const saveMigrationSuccess = (state: SMSesState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveMigrationResult: action.data,
    saveMigrationError: null,
    saveMigrationLoading: false,
  });
};

const saveMigrationFail = (state: SMSesState, action: AppAction<boolean>) => {
  return updateObject(state, {
    saveMigrationResult: null,
    saveMigrationError: action.error,
    saveMigrationLoading: false,
  });
};

const setSelectedChatItem = (state: SMSesState, action: AppAction<SmsChat | undefined>) => {
  return updateObject(state, {
    selectedChatItem: action.data,
  });
};

const clearMessageToInform = (state: SMSesState) => {
  return updateObject(state, {
    messageControlParameters: { ...state.messageControlParameters, messageToInform: '' },
  });
};

const clearMessageControlError = (state: SMSesState) => {
  return updateObject(state, {
    messageControlParameters: { ...state.messageControlParameters, errorMessage: '' },
  });
};

const reducer = (state = initialState, action: Action) => {
  switch (action.type) {
    case actionTypes.SMSES_GET_MESSAGES_START:
      return getMessagesStart(state, action as AppAction<MessagesCollection>);
    case actionTypes.SMSES_GET_MESSAGES_SUCCESS:
      return getMessagesSuccess(state, action as AppAction<MessagesCollection>);
    case actionTypes.SMSES_GET_MESSAGES_FAIL:
      return getMessagesFail(state, action as AppAction<MessagesCollection>);

    case actionTypes.SMSES_SET_MESSAGES:
      return setMessages(state, action as AppAction<MessagesCollection>);

    case actionTypes.SMSES_GET_INSUREDS_PHONES_START:
      return getInsuredsPhonesStart(state, action as AppAction<InsuredPhonesCollection>);
    case actionTypes.SMSES_GET_INSUREDS_PHONES_SUCCESS:
      return getInsuredsPhonesSuccess(state, action as AppAction<InsuredPhonesCollection>);
    case actionTypes.SMSES_GET_INSUREDS_PHONES_FAIL:
      return getInsuredsPhonesFail(state, action as AppAction<InsuredPhonesCollection>);

    case actionTypes.SMSES_SEND_MESSAGE_START:
      return getSendMessageResultStart(state, action as AppAction<SMSe>);
    case actionTypes.SMSES_SEND_MESSAGE_SUCCESS:
      return getSendMessageResultSuccess(state, action as AppAction<SMSe>);
    case actionTypes.SMSES_SEND_MESSAGE_FAIL:
      return getSendMessageResultFail(state, action as AppAction<SMSe>);

    case actionTypes.SMSES_GET_MESSAGE_CONTROL_PARAMETERS_START:
      return getMessageControlParametersStart(state, action as AppAction<MessageControlParameters>);
    case actionTypes.SMSES_GET_MESSAGE_CONTROL_PARAMETERS_SUCCESS:
      return getMessageControlParametersSuccess(state, action as AppAction<MessageControlParameters>);
    case actionTypes.SMSES_GET_MESSAGE_CONTROL_PARAMETERS_FAIL:
      return getMessageControlParametersFail(state, action as AppAction<MessageControlParameters>);

    case actionTypes.SMSES_SET_MESSAGE_CONTROL_PARAMETERS_SUCCESS:
      return setMessageControlParameters(state, action as AppAction<MessageControlParameters>);

    case actionTypes.SMSES_GET_ALL_INSURED_NUMBERS_START:
      return getAllInsuredNumbersStart(state, action as AppAction<InsuredPhoneNumbersCollection>);
    case actionTypes.SMSES_GET_ALL_INSURED_NUMBERS_SUCCESS:
      return getAllInsuredNumbersSuccess(state, action as AppAction<InsuredPhoneNumbersCollection>);
    case actionTypes.SMSES_GET_ALL_INSURED_NUMBERS_FAIL:
      return getAllInsuredNumbersFail(state, action as AppAction<InsuredPhoneNumbersCollection>);

    case actionTypes.SMSES_GET_UPDATES_START:
      return getUpdatesStart(state, action as AppAction<GetUpdatesResult>);
    case actionTypes.SMSES_GET_UPDATES_SUCCESS:
      return getUpdatesSuccess(state, action as AppAction<GetUpdatesResult>);
    case actionTypes.SMSES_GET_UPDATES_FAIL:
      return getUpdatesFail(state, action as AppAction<GetUpdatesResult>);

    case actionTypes.SMSES_SET_LOCAL_MESSAGES:
      return setLocalMessages(state, action as AppAction<MessagesCollection>);

    case actionTypes.SMSES_SEND_MASS_MESSAGES_START:
      return sendMassMessagesStart(state, action as AppAction<boolean>);
    case actionTypes.SMSES_SEND_MASS_MESSAGES_SUCCESS:
      return sendMassMessagesSuccess(state, action as AppAction<boolean>);
    case actionTypes.SMSES_SEND_MASS_MESSAGES_FAIL:
      return sendMassMessagesFail(state, action as AppAction<boolean>);

    case actionTypes.SMSES_SET_UPDATES:
      return setUpdates(state, action as AppAction<GetUpdatesResult>);

    case actionTypes.SMSES_CHECK_PHONE_NUMBER_START:
      return checkPhoneNumberStart(state, action as AppAction<CheckPhoneNumberResult>);
    case actionTypes.SMSES_CHECK_PHONE_NUMBER_SUCCESS:
      return checkPhoneNumberSuccess(state, action as AppAction<CheckPhoneNumberResult>);
    case actionTypes.SMSES_CHECK_PHONE_NUMBER_FAIL:
      return checkPhoneNumberFail(state, action as AppAction<CheckPhoneNumberResult>);

    case actionTypes.SMSES_START_MIGRATION_START:
      return startMigrationStart(state, action as AppAction<SmsMigrationData>);
    case actionTypes.SMSES_START_MIGRATION_SUCCESS:
      return startMigrationSuccess(state, action as AppAction<SmsMigrationData>);
    case actionTypes.SMSES_START_MIGRATION_FAIL:
      return startMigrationFail(state, action as AppAction<SmsMigrationData>);

    case actionTypes.SMSES_SAVE_MIGRATION_START:
      return saveMigrationStart(state, action as AppAction<boolean>);
    case actionTypes.SMSES_SAVE_MIGRATION_SUCCESS:
      return saveMigrationSuccess(state, action as AppAction<boolean>);
    case actionTypes.SMSES_SAVE_MIGRATION_FAIL:
      return saveMigrationFail(state, action as AppAction<boolean>);

    case actionTypes.SMSES_SET_SELECTED_CHAT_ITEM:
      return setSelectedChatItem(state, action as AppAction<SmsChat>);

    case actionTypes.SMSES_CLEAR_MESSAGE_TO_INFORM:
      return clearMessageToInform(state);

    case actionTypes.SMSES_CLEAR_MESSAGE_CONTROL_ERROR:
      return clearMessageControlError(state);

    default:
      return state;
  }
};

export default reducer;
